import { FC, useMemo } from 'react';
import { Box, Stack, Theme, Typography, useMediaQuery } from '@mui/material';
import { Link, useLocation } from 'react-router-dom';
import { HeaderMenu } from '../HeaderMenu';
import { AccountDropdown } from '../AccountDropdown';
import { IAccountMenu, IMenuItem } from '../types';
import { EmbeddedSubNav } from './EmbeddedSubNav';
import { BrandLogoWhite } from '../../Logos';

export interface IPortalNavShellV2Props {
    menuItems: IMenuItem[];
    accountMenu: IAccountMenu;
}

export const LEFT_RAIL_WIDTH = 220;
const MOBILE_DRAWER_WIDTH = 280;

const renderRailLink = (item: IMenuItem, selected: boolean) => (
    <Link
        key={item.path || item.name}
        to={item.path}
        data-testid={`primary-nav-option-${item.testId ?? item.name}`}
        className="portal-nav-link"
        aria-current={selected ? 'page' : undefined}
        style={{
            textDecoration: 'none',
            color: 'inherit',
            display: 'block',
            cursor: 'pointer',
        }}
    >
        <Box
            sx={{
                mx: 1.5,
                mb: 0.5,
                px: 2,
                py: 1.5,
                borderRadius: '8px',
                bgcolor: selected ? 'common.white' : 'transparent',
                transition: 'background-color 0.15s ease',
                '&:hover': {
                    bgcolor: selected ? 'common.white' : 'rgba(255,255,255,0.12)',
                },
            }}
        >
            <Typography
                variant="body1"
                sx={{
                    color: selected ? 'primary.main' : 'common.white',
                    fontWeight: selected ? 600 : 400,
                }}
            >
                {item.name}
            </Typography>
        </Box>
    </Link>
);

/**
 * Brand-V2 navigation shell.
 *
 * Layout (desktop):
 *   +-----------------+----------------------------------------+
 *   | Left primary    | White top header (page title + acct)   |
 *   | nav (colored)   +----------------------------------------+
 *   |                 | Embedded sub-nav (from menuItem.subMenu)|
 *   |                 +----------------------------------------+
 *   |                 | {children}                              |
 *   +-----------------+----------------------------------------+
 *
 * The shell is a drop-in alternative to TransitionalHeaderMenu � it accepts
 * the exact same { menuItems, accountMenu } contract, so HeaderContainer can
 * branch on useBrandVersion() without reshaping any data. Sub-nav items are
 * read directly off IMenuItem.subMenu, which HeaderContainer already populates
 * via menuVisibilityMapper / filterVisibleSubMenus, so visibility logic is
 * reused as-is.
 *
 * On screens below `md` we fall back to the existing mobile drawer
 * (HeaderMenu) � redesigning mobile is out of scope for this POC.
 */
export const PortalNavShellV2: FC<IPortalNavShellV2Props> = ({ menuItems, accountMenu, children }) => {
    const isMdDown = useMediaQuery((t: Theme) => t.breakpoints.down('md'));
    const { pathname } = useLocation();

    const activeMenuItem = useMemo<IMenuItem | undefined>(
        () => menuItems.find((item) => item.path && pathname.includes(item.path)),
        [menuItems, pathname]
    );

    // Fall back to the section name (e.g. anywhere under /settings -> "Settings") when the
    // route isn't represented in the primary nav, so the page-title slot isn't blank.
    const fallbackTitle = useMemo(() => {
        if (pathname.toLowerCase().includes('/settings')) {
            return 'Settings';
        }
        return '';
    }, [pathname]);

    const displayTitle = activeMenuItem?.pageTitle ?? activeMenuItem?.name ?? fallbackTitle;

    if (isMdDown) {
        return (
            <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
                <HeaderMenu menuItems={menuItems} accountMenu={accountMenu} drawerWidth={MOBILE_DRAWER_WIDTH} />
                <Box sx={{ mt: '64px', flex: 1, display: 'flex', flexDirection: 'column' }}>{children}</Box>
            </Box>
        );
    }

    return (
        <Box
            data-testid="portal-nav-shell-v2"
            sx={{
                display: 'flex',
                flexDirection: 'row',
                minHeight: '100vh',
                alignItems: 'stretch',
            }}
        >
            {/* LEFT PRIMARY NAV � full viewport height so primary color runs to the bottom */}
            <Box
                component="aside"
                data-testid="portal-nav-shell-v2-rail"
                sx={{
                    width: LEFT_RAIL_WIDTH,
                    flexShrink: 0,
                    minHeight: '100vh',
                    bgcolor: 'primary.main',
                    color: 'common.white',
                    display: 'flex',
                    flexDirection: 'column',
                    borderRight: '2px solid rgba(255,255,255,0.2)',
                }}
            >
                <Box
                    sx={{
                        height: 72,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        px: 2,
                        borderBottom: '2px solid rgba(255,255,255,0.2)',
                    }}
                >
                    <BrandLogoWhite />
                </Box>
                <Stack component="nav" sx={{ py: 2, px: 0, flex: 1 }}>
                    {menuItems.map((item) => renderRailLink(item, activeMenuItem?.path === item.path))}
                </Stack>
            </Box>

            {/* RIGHT CONTENT COLUMN � fills viewport height so footer can sit at bottom */}
            <Box
                sx={{
                    flex: 1,
                    minWidth: 0,
                    minHeight: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    alignSelf: 'stretch',
                }}
            >
                {/* WHITE TOP HEADER */}
                <Stack
                    component="header"
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    data-testid="portal-nav-shell-v2-header"
                    sx={{
                        height: 72,
                        bgcolor: 'background.paper',
                        borderBottom: 1,
                        borderColor: 'divider',
                        px: 4,
                    }}
                >
                    {displayTitle ? (
                        <Typography component="h1" variant="h5" sx={{ color: 'text.primary', fontWeight: 600 }}>
                            {displayTitle}
                        </Typography>
                    ) : (
                        // Spacer keeps the account dropdown right-aligned (space-between) when there's no title
                        <span />
                    )}
                    <AccountDropdown accountMenu={accountMenu} onLightBackground />
                </Stack>

                {/* EMBEDDED SUB-NAV (just below the white header) */}
                <EmbeddedSubNav subMenu={activeMenuItem?.subMenu} />

                {/* PAGE CONTENT */}
                <Box
                    sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', minWidth: 0, width: '100%' }}
                >
                    {children}
                </Box>
            </Box>
        </Box>
    );
};
